import React from 'react';
import {
  getLeaveSummaryUsedQuotaDisplay,
  formatDateDisplay,
  LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS,
  viewLeaveAttachmentFile,
} from './leaveUtils';

export interface LeaveSummaryModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  data: {
    leaveType: string;
    leaveTypeCode?: string;
    startDate: string;
    endDate: string;
    formattedStartDate: string;
    formattedEndDate: string;
    leaveDays: number;
    absencehours: string;
    processor: string;
    newNote?: string;
    dischargeDate?: string | null;
    expectedDueDate?: string | null;
    formattedDischargeDate?: string;
    formattedExpectedDueDate?: string;
    showDischargeInAdditionalSummary?: boolean;
    locationOfAccident?: string;
    leaveReasonDropdown?: string;
    dayType?: string;
    isSterilizationOfSelf?: boolean;
    additionalFieldsConfig?: any[];
    validationData?: any;
    attachments: Array<{
      id: number;
      name: string;
      type: string;
      url?: string;
      fileName: string;
      fileExtension: string;
      fileSize: string;
    }>;
  };
  alerts?: Array<{ message: string; type?: 'info' | 'warning' | 'danger' }>;
}

const LeaveSummaryModal: React.FC<LeaveSummaryModalProps> = ({
  show,
  onClose,
  onConfirm,
  isSubmitting = false,
  data,
  alerts = [],
}) => {
  if (!show) return null;

  const usedQuotaLine = getLeaveSummaryUsedQuotaDisplay({
    leaveType: data.leaveType,
    leaveDays: data.leaveDays,
    absenceTypeCode: data.leaveTypeCode,
  });

  const getAbsenceHoursDisplay = () => {
    if (data.validationData?.WorkingHoursDuration) {
      const parsed = parseFloat(data.validationData.WorkingHoursDuration);
      return !isNaN(parsed) ? `${parsed.toFixed(2)} hrs` : data.validationData.WorkingHoursDuration;
    }
    if (data.absencehours && data.absencehours !== '0:00') {
      return `${data.absencehours} hrs`;
    }
    return null;
  };

  const getFileIcon = (fileType: string) => {
    const ext = fileType?.toLowerCase() || '';
    switch (ext) {
      case 'pdf':
        return 'ti ti-file-type-pdf text-danger';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'ti ti-file-type-jpg text-primary';
      case 'doc':
      case 'docx':
        return 'ti ti-file-type-doc text-info';
      default:
        return 'ti ti-file text-secondary';
    }
  };

  const formatFileSize = (size: string | number) => {
    if (!size) return '0 KB';
    const str = String(size);
    if (str.includes('KB') || str.includes('MB')) return str;
    const num = Number(str);
    if (isNaN(num)) return str;
    return `${(num / 1024).toFixed(1)} KB`;
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 1055 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content shadow-lg border-0 rounded-3">
          {/* Header */}
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(135deg, #A93226 0%, #C0392B 100%)',
              borderTopLeftRadius: 'calc(0.5rem - 1px)',
              borderTopRightRadius: 'calc(0.5rem - 1px)',
            }}
          >
            <div className="d-flex align-items-center">
              <div
                className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center me-2"
                style={{ width: '36px', height: '36px' }}
              >
                <i className="ti ti-file-description fs-20 text-white"></i>
              </div>
              <div>
                <h5 className="modal-title fw-bold text-white mb-0">Leave Request Summary</h5>
                <small className="text-white-50">Please review your leave details before final submission</small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-4" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {/* Alerts */}
            {alerts && alerts.length > 0 && (
              <div className="mb-4">
                {alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    className={`alert alert-${
                      alert.type === 'danger' ? 'danger' : alert.type === 'warning' ? 'warning' : 'info'
                    } d-flex align-items-center shadow-sm mb-2 rounded-2`}
                    role="alert"
                  >
                    <i className="ti ti-alert-triangle fs-20 me-2"></i>
                    <div>{alert.message}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Type of Leave Section */}
            <div className="card mb-3 border bg-light shadow-sm">
              <div className="card-body py-3 px-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                      Type of Leave
                    </span>
                    <h5 className="fw-bold text-dark mb-0 mt-1">{data.leaveType}</h5>
                  </div>
                  {data.leaveTypeCode && (
                    <span className="badge bg-primary fs-12 px-3 py-2 rounded-pill">
                      Code: {data.leaveTypeCode}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* General Data Section */}
            <div className="card mb-3 border shadow-sm">
              <div className="card-header bg-white py-2 border-bottom">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center">
                  <i className="ti ti-calendar-event me-2 text-primary"></i>
                  General Data
                </h6>
              </div>
              <div className="card-body p-3">
                <div className="row g-3">
                  <div className="col-sm-6 col-md-4">
                    <span className="text-muted d-block small">Start Date</span>
                    <strong className="text-dark fs-14">{data.formattedStartDate || data.startDate}</strong>
                  </div>
                  <div className="col-sm-6 col-md-4">
                    <span className="text-muted d-block small">End Date</span>
                    <strong className="text-dark fs-14">{data.formattedEndDate || data.endDate}</strong>
                  </div>
                  <div className="col-sm-6 col-md-4">
                    <span className="text-muted d-block small">Leave Duration</span>
                    <span className="badge bg-info-subtle text-info border border-info-subtle px-2 py-1 fs-13 fw-semibold">
                      {data.leaveDays} {data.leaveDays === 1 ? 'Calendar Day' : 'Calendar Days'}
                    </span>
                  </div>

                  {getAbsenceHoursDisplay() && (
                    <div className="col-sm-6 col-md-4">
                      <span className="text-muted d-block small">Absence Hours</span>
                      <strong className="text-dark fs-14">{getAbsenceHoursDisplay()}</strong>
                    </div>
                  )}

                  <div className="col-sm-6 col-md-4">
                    <span className="text-muted d-block small">Processor / Approver</span>
                    <strong className="text-dark fs-14">
                      {data.validationData?.ApproverName || data.processor || 'N/A'}
                    </strong>
                  </div>

                  {/* Quota Deduction Info */}
                  <div className="col-12">
                    <span className="text-muted d-block small">Quota Consumption</span>
                    <div className="p-2 bg-light rounded border text-secondary fs-13 mt-1">
                      {data.validationData?.DeductionInfo ? (
                        <span>
                          <i className="ti ti-info-circle me-1 text-primary"></i>
                          Used: {data.validationData.DeductionInfo.trim()}
                        </span>
                      ) : usedQuotaLine ? (
                        <span>
                          <i className="ti ti-info-circle me-1 text-primary"></i>
                          {usedQuotaLine}
                        </span>
                      ) : (
                        <span>
                          <i className="ti ti-info-circle me-1 text-muted"></i>
                          Standard quota balance will be consumed upon manager approval.
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Note */}
                  <div className="col-12">
                    <span className="text-muted d-block small">Note / Reason</span>
                    <div className="p-2 bg-light rounded border text-dark fs-13 mt-1">
                      {data.validationData?.Notes || data.newNote || 'None'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Data Section */}
            {data.additionalFieldsConfig && data.additionalFieldsConfig.length > 0 && (
              <div className="card mb-3 border shadow-sm">
                <div className="card-header bg-white py-2 border-bottom">
                  <h6 className="fw-bold text-dark mb-0 d-flex align-items-center">
                    <i className="ti ti-list-details me-2 text-primary"></i>
                    Additional Data
                  </h6>
                </div>
                <div className="card-body p-3">
                  <div className="row g-3">
                    {data.additionalFieldsConfig.map((field: any, idx: number) => {
                      if (!field.FieldLabel || LEAVE_ADDITIONAL_FIELDS_HIDDEN_KEYS.includes(field.Fieldname)) {
                        return null;
                      }

                      const fieldName = field.Fieldname;
                      let displayVal = data.validationData?.AdditionalFields?.[fieldName];

                      if (displayVal === undefined || displayVal === null || displayVal === '') {
                        if (fieldName === 'CUSTOMER01') displayVal = (data as any).reasonForLeave1;
                        else if (fieldName === 'CUSTOMER02') displayVal = data.locationOfAccident;
                        else if (fieldName === 'CUSTOMER03') displayVal = (data as any).reasonForLeave2;
                        else if (fieldName === 'CUSTOMER05') displayVal = (data as any).cityDistrict;
                        else if (fieldName === 'CUSTOMER06') displayVal = (data as any).hospitalizationStatus;
                        else if (fieldName === 'CUSTOMER07') displayVal = (data as any).addressLine3;
                        else if (fieldName === 'CUSTOMER08') displayVal = data.isSterilizationOfSelf ? 'Yes' : 'No';
                        else if (fieldName === 'CUSTOMER09') displayVal = data.leaveReasonDropdown;
                        else if (fieldName === 'CUSTOMER10') displayVal = data.dayType;
                        else if (fieldName === 'RMDDA') {
                          displayVal = data.formattedDischargeDate || data.formattedExpectedDueDate || data.dischargeDate || data.expectedDueDate;
                        }
                      }

                      if (fieldName === 'ABRST') {
                        const wh = data.validationData?.WorkingHoursDuration || data.validationData?.workinghourduration;
                        if (wh) displayVal = `${parseFloat(wh).toFixed(2)} hrs`;
                      }

                      if (displayVal === 'X' || displayVal === true) displayVal = 'Yes';
                      if (!displayVal) displayVal = 'None';

                      return (
                        <div className="col-sm-6 col-md-4" key={idx}>
                          <span className="text-muted d-block small">{field.FieldLabel}</span>
                          <strong className="text-dark fs-14">{displayVal}</strong>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Attachments Section */}
            <div className="card mb-0 border shadow-sm">
              <div className="card-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center">
                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center">
                  <i className="ti ti-paperclip me-2 text-primary"></i>
                  Attachments ({data.attachments?.length || 0})
                </h6>
              </div>
              <div className="card-body p-3">
                {data.attachments && data.attachments.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Document Name</th>
                          <th style={{ width: '90px' }}>Type</th>
                          <th style={{ width: '110px' }}>Size</th>
                          <th style={{ width: '80px' }} className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.attachments.map((att) => (
                          <tr key={att.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className={`${getFileIcon(att.type || att.fileExtension)} fs-18 me-2`}></i>
                                <span className="fw-medium text-dark">{att.name}</span>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-secondary-subtle text-secondary text-uppercase fs-11">
                                {att.type || att.fileExtension || 'FILE'}
                              </span>
                            </td>
                            <td className="text-muted fs-12">{formatFileSize(att.fileSize)}</td>
                            <td className="text-center">
                              {att.url ? (
                                <button
                                  type="button"
                                  onClick={() => viewLeaveAttachmentFile(att.url!)}
                                  className="btn btn-sm btn-outline-primary py-0 px-2 fs-12"
                                  title="View Attachment"
                                >
                                  <i className="ti ti-eye me-1"></i>View
                                </button>
                              ) : (
                                <span className="text-muted fs-12">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-muted text-center py-2 fs-13">
                    <i className="ti ti-file-off me-1"></i>No attachments attached
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer bg-light py-2 px-3 border-top d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-outline-secondary px-4"
              onClick={onClose}
              disabled={isSubmitting}
            >
              <i className="ti ti-arrow-left me-1"></i>
              Back / Edit
            </button>
            <button
              type="button"
              className="btn text-white px-4"
              style={{
                backgroundColor: '#A93226',
                borderColor: '#A93226',
              }}
              onClick={onConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submitting Leave...
                </>
              ) : (
                <>
                  <i className="ti ti-send me-1"></i>
                  Confirm & Submit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveSummaryModal;
